import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import LoadingScreen from '../common/LoadingScreen';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';

function RequireAuth({ children }) {
  const { currentUser, loading: authLoading, subscriptionStatus, isGrandfathered } = useAuth();
  const [status, setStatus] = useState('checking'); // 'checking', 'authorized', 'needs_profile', 'needs_subscription'
  const location = useLocation();
  const { getSubscriptionStatus, getUserType, isLapsed, isNeverSubscribed, checkingHistory } = useFeatureAccess();

  // Allow profile completion without subscription check
  const isProfileRoute = location.pathname === '/profile';

  useEffect(() => {
    // If auth is still loading or we're still checking subscription history, we can't do anything yet.
    if (authLoading || checkingHistory) {
      setStatus('checking');
      return;
    }

    // If there's no user, the decision is made.
    if (!currentUser) {
      setStatus('needs_login');
      return;
    }

    // If we have a user, check their profile and subscription status.
    const checkUserAccess = async () => {
      try {
        // First check if user has complete profile
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        if (!userDoc.exists()) {
          setStatus('needs_profile');
          return;
        }

        const userData = userDoc.data();
        
        // Check if Google user needs to complete registration
        if (userData.needsRegistrationCompletion) {
          setStatus('needs_registration_completion');
          return;
        }

        // Check if email user needs to complete profile
        if (userData.needsProfileCompletion) {
          setStatus('needs_registration_completion');
          return;
        }
        
        if (!userData.dateOfBirth) {
          setStatus('needs_profile');
          return;
        }

        // Allow profile route access even without subscription (for profile completion)
        if (isProfileRoute) {
          setStatus('authorized');
          return;
        }

        // Then check subscription status for all other routes
        const userType = getUserType();
        
        // Allow access for grandfathered users
        if (isGrandfathered || userType === 'grandfathered') {
          setStatus('authorized');
          return;
        }

        // Allow access for active subscribers and trial users
        if (userType === 'active') {
          setStatus('authorized');
          return;
        }

        // Allow access for lapsed users (they get basic functionality)
        if (userType === 'lapsed') {
          setStatus('authorized');
          return;
        }

        // Former complimentary users keep BASIC authenticated access, like
        // lapsed users — never a registration redirect for this state.
        if (userType === 'complimentary_expired') {
          setStatus('authorized');
          return;
        }

        // A required read failed: hold the defensive checking state instead
        // of guessing. Routing to registration off a failed read could dump a
        // real member into the signup flow.
        if (userType === 'unknown') {
          setStatus('checking');
          return;
        }

        // Treat never-subscribed users as having incomplete registration
        // They need to complete Step 3 (trial setup)
        if (userType === 'never_subscribed') {
          setStatus('needs_registration_completion');
          return;
        }

        // Fallback for any unexpected states
        setStatus('needs_subscription');
        
      } catch (error) {
        console.error('RequireAuth - Error checking user access:', error);
        // On error, redirect to profile to be safe
        setStatus('needs_profile');
      }
    };

    checkUserAccess();
  }, [currentUser, authLoading, subscriptionStatus, isGrandfathered, getUserType, checkingHistory, isProfileRoute]);

  // Render based on the explicit status
  if (status === 'checking' || authLoading) {
    return <LoadingScreen />;
  }

  if (status === 'needs_login') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (status === 'needs_profile') {
    return <Navigate to="/profile" state={{ from: location }} replace />;
  }

  if (status === 'needs_registration_completion') {
    return <Navigate to="/complete-registration" state={{ from: location }} replace />;
  }

  if (status === 'needs_subscription') {
    // Redirect to subscription activation with context about where they were trying to go
    return <Navigate to="/subscribe" state={{ from: location, reason: 'subscription_required' }} replace />;
  }

  if (status === 'authorized') {
    return children;
  }

  // Fallback case, should not be reached.
  return <LoadingScreen />;
}

export default RequireAuth; 